import {
  DoorClosed,
  Compass,
  Layers,
  HandHelping,
  Handshake,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import { PrimaryCTA, SecondaryCTA } from "@/components/CTAButtons";

const CHALLENGES = [
  {
    icon: DoorClosed,
    title: "Limited access to major buyers",
    body:
      "Procurement gatekeepers are hard to reach — even when your offer is genuinely differentiated.",
  },
  {
    icon: Compass,
    title: "Weak positioning in new markets",
    body:
      "Entering a new geography without a local trust signal usually means starting from zero.",
  },
  {
    icon: Layers,
    title: "Difficulty differentiating similar offers",
    body:
      "When everyone claims the same specs, decisions tilt toward the supplier with relationships, not the better product.",
  },
  {
    icon: HandHelping,
    title: "Lack of trusted intermediary support",
    body:
      "Cold outreach erodes brand. A relationship-led intermediary changes the conversation.",
  },
];

export default function ForSuppliers() {
  return (
    <div data-testid="for-suppliers-page">
      <PageHero
        eyebrow="For Suppliers"
        title={
          <>
            Better buyer access
            <br />
            for <span className="text-brand-olive">qualified suppliers</span>.
          </>
        }
        subtitle="IngredientSignal represents a selective set of suppliers to FFIB's buyer network — through verified positioning, targeted outreach, and warm introductions managed by analysts."
      >
        <div className="flex flex-wrap gap-4">
          <PrimaryCTA to="/get-started?audience=supplier" testId="suppliers-hero-cta">
            Request Supplier Representation
          </PrimaryCTA>
          <SecondaryCTA to="/platform" testId="suppliers-hero-secondary">
            How the platform supports this
          </SecondaryCTA>
        </div>
      </PageHero>

      {/* Challenges */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="Supplier Challenges"
            title="Open marketplaces flatten your value. Relationships don't."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {CHALLENGES.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  data-testid={`suppliers-challenge-${c.title.toLowerCase().split(" ")[0]}`}
                  className="bg-brand-ivory border border-brand-border p-10 hover:border-brand-muted/40 transition-colors"
                >
                  <Icon
                    className="w-6 h-6 text-brand-olive"
                    strokeWidth={1.4}
                  />
                  <h3 className="font-display mt-6 text-xl text-brand-deep font-medium">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-brand-muted leading-relaxed font-light">
                    {c.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What suppliers get */}
      <section className="py-24 lg:py-32 bg-brand-linen border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="What Suppliers Get"
              title="A relationship-led path into qualified buyer programs."
              intro="No bulk listings. No commodity comparisons. Just deliberate positioning, managed by FFIB."
            />
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-brand-border border-y border-brand-border">
              {[
                "Selective representation through FFIB's relationship network",
                "Tailored positioning briefs for target buyer categories",
                "Warm introductions to procurement decision-makers",
                "Conference and market intelligence relevant to your offer",
                "Analyst support during qualification and follow-up",
              ].map((line, idx) => (
                <li
                  key={line}
                  data-testid={`suppliers-get-${idx}`}
                  className="py-6 flex items-start gap-6"
                >
                  <span className="font-mono text-xs text-brand-olive tracking-[0.2em] pt-1">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <p className="text-brand-warm leading-relaxed font-light">
                    {line}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Why relationship-led */}
      <section className="py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-start gap-5 border-l-2 border-brand-olive pl-6">
            <Handshake
              className="w-5 h-5 text-brand-olive mt-1.5 flex-shrink-0"
              strokeWidth={1.5}
            />
            <div>
              <h3 className="font-display text-2xl text-brand-deep font-light leading-snug">
                Why relationship-led outreach matters.
              </h3>
              <p className="mt-4 text-brand-warm leading-relaxed font-light">
                Specialty ingredient buyers don't take cold meetings — they
                take warm ones. A trusted intermediary changes both who hears
                your story and how seriously they listen. FFIB only represents
                suppliers where we believe we can credibly carry that message.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-brand-border bg-brand-linen/70">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl sm:text-4xl text-brand-deep font-light leading-tight">
            Ready to be represented properly?
          </h2>
          <p className="mt-5 text-brand-muted max-w-2xl font-light">
            Share your category, geographies, and target buyers. FFIB will
            assess fit and respond.
          </p>
          <div className="mt-8">
            <PrimaryCTA to="/get-started?audience=supplier" testId="suppliers-bottom-cta">
              Request Supplier Representation
            </PrimaryCTA>
          </div>
        </div>
      </section>
    </div>
  );
}
